import { queryOptions, useQuery } from '@tanstack/react-query';
import Fuse, { type FuseResultMatch } from 'fuse.js';
import { getFriends } from '@/hooks/contacts/use-friends';
import type { Friend } from '@/models/user/friend';
import type { Tag } from '@/models/user/tag';
import { getGroupedTagsByMember } from '../tags/use-tags';

export const friendsWithTagsQueryOptions = queryOptions<Array<Friend & { Tags: Tag[] }>>({
  queryKey: ['/GetFriendsWithTags'],
  queryFn: async ({ signal }) => {
    const friends = await getFriends(signal);
    const tagsByMember = await getGroupedTagsByMember(signal);

    return Object.values(friends).map((friend) => ({ ...friend, Tags: tagsByMember[friend.FriendID] ?? [] }));
  },
  staleTime: 0,
});

export type SearchFriend = Friend & {
  Tags: Tag[];
  FuzzySearch?: {
    highlightRanges: readonly FuseResultMatch[] | undefined;
    score?: number;
  };
};

interface Options {
  searchIncludeTags?: boolean;
}

export default function useSearchFriends(keyword = '', { searchIncludeTags }: Options = { searchIncludeTags: false }) {
  const { data: friendsWithTags = [], ...query } = useQuery(friendsWithTagsQueryOptions);

  const keys = searchIncludeTags ? ['RemarkName', 'FriendNickName', 'Tags.TagName'] : ['RemarkName', 'FriendNickName'];
  const fuse = new Fuse(friendsWithTags, {
    includeScore: true,
    includeMatches: true,
    threshold: 0.3,
    shouldSort: true,
    keys,
  });

  const fuseSearchResults = fuse.search(keyword).map(({ item, matches, score }) => ({
    ...item,
    FuzzySearch: { highlightRanges: matches, score },
  }));

  return {
    friends: friendsWithTags,
    friendCount: friendsWithTags.length,
    filteredFriends: keyword ? fuseSearchResults : friendsWithTags,
    keyword,
    ...query,
  };
}
